// Iter152 · Split extraído de OfertasComercial.jsx (líneas 177–871).
// Contiene OfertasList (tabla, filtros, columnas persistidas, publicación).

import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, BarChart3, Bot, Check, ChevronDown, ExternalLink, FileSpreadsheet, Globe,
  Link2, Loader2, Pencil, Plus, RefreshCw, Search, Send, Settings, ShieldCheck,
  Sparkles, Trash2, X, History,
} from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";
import CompetitiveAnalysisModal from "@/components/CompetitiveAnalysisModal";
import MergeSuggestionsModal from "@/components/offer/MergeSuggestionsModal";
import ComplianceCheckModal from "@/components/ComplianceCheckModal";
import CompletitudBulkModal from "@/components/offer/CompletitudBulkModal";
import { useDGFilter } from "@/components/DGFilter";
import {
  ALL_COLS, COLS_POR_ESTADO, VISTAS_PREDEFINIDAS, ESTADOS_OPCIONES, FASES_ADJ,
  ACTIVIDADES_POR_DG, ALL_ACTIVIDADES, ESTADO_CLS, FASE_CLS, computeIRR, computeUteRisk,
  isUte, formatUteLabel, fmtFechaOferta, CAN_DELETE_ROLES,
} from "./shared";
import { OfertaForm, PendingMotivoModal, PendingConfirmacionPresentacionModal, ImportFromExternalModal } from "./OfertaForm";
import { HelpTip } from "@/components/HelpTip";
import { ImporteRangeFilter } from "@/components/ImporteRangeFilter";
import SavedViewsBar from "@/components/crm/SavedViewsBar";
import ActiveFilterChips from "@/components/crm/ActiveFilterChips";
import ResponsablePicker from "@/components/offer/ResponsablePicker";


// CC04 · Bandeja de PRESENTADAS sin fase · propone fase derivada de un evento
// registrado (requiere confirmar en ficha); no auto-aplica ni pone fase por defecto.
function FasesSinAsignarTray({ onOpen }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/fases-sin-asignar`).then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.total_sin_fase) return null;
  return (
    <div className="mb-2 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2" data-testid="oc-fases-sin-asignar-tray">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 text-[12px] font-semibold text-amber-800" data-testid="oc-fases-sin-asignar-toggle">
        <AlertTriangle size={13}/>
        {data.total_sin_fase} presentadas sin fase
        <span className="font-normal text-amber-700">· {data.derivables_por_evento} derivables por evento · {data.sin_evidencia} sin evidencia</span>
        <ChevronDown size={12} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid="oc-fases-sin-asignar-detail">
          <p className="text-[10px] text-amber-700">{data.nota}</p>
          {data.derivables.map(r => (
            <div key={r.id} className="flex items-center gap-2 text-[11px] bg-white rounded px-2 py-1 border border-amber-100" data-testid={`oc-fase-derivable-${r.id}`}>
              <span className="font-semibold text-gray-700 truncate max-w-[200px]">{r.cliente}</span>
              <span className="text-gray-400">{r.expediente}</span>
              <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[10px] font-semibold shrink-0">→ {r.fase_sugerida}</span>
              <span className="text-gray-500 italic truncate">{r.evidencia}</span>
              <button type="button" onClick={() => onOpen(r.id)} className="ml-auto text-[10px] font-semibold text-[#007934] hover:underline shrink-0" data-testid={`oc-fase-open-${r.id}`}>Confirmar en ficha →</button>
            </div>
          ))}
          {data.sin_evidencia > 0 && (
            <p className="text-[10px] text-gray-500">{data.sin_evidencia} presentadas sin fase y sin evento registrado para derivarla — necesitan un dato de apertura/adjudicación/firma antes de asignar fase. No se pone «Pendiente de apertura» por defecto.</p>
          )}
        </div>
      )}
    </div>
  );
}

// CC03 · Bandeja "Información por completar" · agrega huecos (ausente/derivable)
// de la cartera reutilizando la procedencia (CC06). No rellena nada.
function CompletitudTray({ onOpen, onBulk }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/completitud?scope=todas`).then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.total_ofertas_con_huecos) return null;
  const topFields = (data.fields || []).filter(f => f.ausente > 0).slice(0, 8);
  return (
    <div className="mb-2 border border-sky-200 bg-sky-50 rounded-lg px-3 py-2" data-testid="oc-completitud-tray">
      <div className="w-full flex items-center gap-2">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-[12px] font-semibold text-sky-800 flex-1 min-w-0" data-testid="oc-completitud-toggle">
          <ShieldCheck size={13}/>
          Información por completar
          <span className="font-normal text-sky-700 truncate">· {data.total_ofertas_con_huecos} de {data.n_ofertas} ofertas con huecos</span>
        </button>
        {onBulk && (
          <button type="button" onClick={onBulk} data-testid="oc-completitud-bulk-btn"
            className="shrink-0 text-[10px] font-semibold bg-[#007934] text-white px-2 py-1 rounded hover:bg-[#006528] flex items-center gap-1">
            <Sparkles size={11}/> Completar en lote
          </button>
        )}
        <button type="button" onClick={() => setOpen(o => !o)} className="shrink-0 text-sky-800" aria-label="Desplegar">
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
        </button>
      </div>
      {open && (
        <div className="mt-2 grid md:grid-cols-2 gap-3" data-testid="oc-completitud-detail">
          <div>
            <div className="text-[10px] uppercase font-semibold text-sky-700 mb-1">Campos más ausentes</div>
            <div className="space-y-1">
              {topFields.map(f => (
                <div key={f.key} className="flex items-center gap-2 text-[11px] bg-white rounded px-2 py-1 border border-sky-100" data-testid={`oc-completitud-field-${f.key}`}>
                  <span className="font-semibold text-gray-700">{f.label}</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold shrink-0">{f.ausente} ausentes</span>
                  {f.derivable > 0 && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] shrink-0">{f.derivable} derivables</span>}
                  {f.impacto && <span className="text-gray-400 italic truncate">{f.impacto}</span>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold text-sky-700 mb-1">Ofertas con más huecos</div>
            <div className="space-y-1">
              {(data.top_offers || []).slice(0, 8).map(o => (
                <div key={o.id} className="flex items-center gap-2 text-[11px] bg-white rounded px-2 py-1 border border-sky-100" data-testid={`oc-completitud-offer-${o.id}`}>
                  <span className="font-semibold text-gray-700 truncate max-w-[160px]">{o.cliente}</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold shrink-0">{o.n_ausentes} huecos</span>
                  <button type="button" onClick={() => onOpen(o.id)} className="ml-auto text-[10px] font-semibold text-[#007934] hover:underline shrink-0" data-testid={`oc-completitud-open-${o.id}`}>Completar →</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OfertasList() {
  const { user } = useAuth();
  const { dg: globalDG } = useDGFilter(user?.email);
  // Roles que pueden borrar/restaurar (mismo set que el backend require_admin_or_manager).
  const canDelete = !!user && CAN_DELETE_ROLES.includes(user.role);
  const isAdmin = user?.role === "admin";
  const [showPapelera, setShowPapelera] = useState(false);
  const [items, setItems] = useState([]);
  // Iter225 · Contadores globales para KPIs — mismos filtros excepto estado/fase.
  // Así al hacer click en un KPI el resto siguen mostrando el número real.
  const [kpiItems, setKpiItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);  // RCA · evita mostrar "0" cuando en realidad falló la carga (blip de Atlas)
  const [estado, setEstado] = useState(() => {
    try {
      const e = new URLSearchParams(window.location.search).get("estado");
      // Iter166/171 · Default = "" (Todos). Toggle "Solo activas" acota
      // adicionalmente. Antes forzábamos PRESENTADA, pero eso ocultaba
      // ofertas en ESTUDIO/POR DECIDIR/PUBLICADA importadas del Excel.
      // Feedback 2026-10 · los enlaces "Ver Publicadas/Por decidir/En estudio"
      // de MERCADO también deep-linkan a estados de pipeline; se aceptan aquí.
      const DEEPLINK = [...ESTADOS_OPCIONES, "PUBLICADA", "POR DECIDIR", "ESTUDIO", "NO PRESENTADA", "BORRADOR"];
      return (e && DEEPLINK.includes(e)) ? e : "";
    } catch { return ""; }
  });
  const [faseAdj, setFaseAdj] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("fase") || ""; } catch { return ""; }
  });
  // Iter192 · Filtro por rango de fecha de presentación (feedback usuario)
  const [fechaPresDesde, setFechaPresDesde] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("desde") || ""; } catch { return ""; }
  });
  const [fechaPresHasta, setFechaPresHasta] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("hasta") || ""; } catch { return ""; }
  });
  const [direccionGeneral, setDireccionGeneral] = useState(() => globalDG || "all");  // SERVICIOS | TRATAMIENTO | all
  // iter 134 · el filtro global DG (residuos/servicios urbanos) tiene prioridad
  // sobre el select local. Al cambiar el pill se recarga la lista.
  useEffect(() => {
    if (globalDG && globalDG !== direccionGeneral) setDireccionGeneral(globalDG);
    if (!globalDG && direccionGeneral !== "all") setDireccionGeneral("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDG]);
  const [actividad, setActividad] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("actividad") || "all"; } catch { return "all"; }
  });                // C&W | GA | MOB | Light Capex | PPP
  // Iter154 · tipo de contratación: NC | R | ADC. Iter230 · Multi-select
  // (feedback usuario: poder marcar NC+R a la vez). [] = todos.
  const [tipoContrat, setTipoContrat] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("tipo");
      return v ? v.split(",").filter(Boolean) : [];
    } catch { return []; }
  });
  const [tipoContratOpen, setTipoContratOpen] = useState(false);
  const toggleTipoContrat = (v) => setTipoContrat(prev =>
    prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
  );
  const tipoContratKey = tipoContrat.slice().sort().join(",");
  const [q, setQ] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("q") || ""; } catch { return ""; }
  });
  // Iter404 · filtro dedicado por expediente (independiente del buscador general `q`)
  const [expte, setExpte] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("expte") || ""; } catch { return ""; }
  });
  // Iter179.1 · Foco por comercial (filtra por responsables contiene email)
  // Default OFF ("Ver todo"). Cada usuario recuerda su preferencia.
  const [mineOnly, setMineOnly] = useState(() => {
    try {
      const key = `ofertas-mine-${(user?.email || "").toLowerCase()}`;
      const v = localStorage.getItem(key);
      return v === "1";  // default OFF
    } catch { return false; }
  });
  useEffect(() => {
    try {
      const key = `ofertas-mine-${(user?.email || "").toLowerCase()}`;
      localStorage.setItem(key, mineOnly ? "1" : "0");
    } catch { /* no-op */ }
  }, [mineOnly, user?.email]);
  // Iter157 · Deep-link desde MERCADO → aísla la oferta destacada.
  const [highlightId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("highlight") || ""; }
    catch { return ""; }
  });
  const [editing, setEditing] = useState(null);
  const [sortBy, setSortBy] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("sortBy") || "fecha_presentacion"; } catch { return "fecha_presentacion"; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("sortDir") === "1" ? 1 : -1; } catch { return -1; }
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const [colMenuAlign, setColMenuAlign] = useState("right"); // abre hacia el lado con sitio
  const [showImportModal, setShowImportModal] = useState(false);
  const [publishing, setPublishing] = useState(null); // {id, target_estado}
  const [showCompetitive, setShowCompetitive] = useState(null); // oferta object
  const [showCompliance, setShowCompliance]   = useState(null); // oferta object
  const [showBulk, setShowBulk] = useState(false); // CC03/CC06 · completar cartera en lote
  const [healthStats, setHealthStats] = useState(null);
  const [arrastreIds, setArrastreIds] = useState(() => new Set());
  const [equipo, setEquipo] = useState([]);
  const [showMotivoModal, setShowMotivoModal] = useState(false);
  const [showConfirmacionModal, setShowConfirmacionModal] = useState(false);
  const [irrMin, setIrrMin] = useState(() => {
    try {
      const v = localStorage.getItem("oc_irr_min");
      return v != null ? Number(v) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_irr_min", String(irrMin)); } catch {}
  }, [irrMin]);
  // Iter282 · filtro de importe (M€) persistente — pedido explícito del usuario
  const [importeMin, setImporteMin] = useState(() => {
    try {
      const v = localStorage.getItem("oc_importe_min");
      return v != null ? Number(v) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_importe_min", String(importeMin)); } catch {}
  }, [importeMin]);
  const [importeMax, setImporteMax] = useState(() => {
    try {
      const v = localStorage.getItem("oc_importe_max");
      return v != null ? Number(v) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_importe_max", String(importeMax)); } catch {}
  }, [importeMax]);
  // CRM-R05 · Vistas guardadas + chips de filtros activos para Licitaciones.
  const ocFilters = {
    q, expte, estado, faseAdj, fechaPresDesde, fechaPresHasta,
    direccionGeneral, actividad, tipoContrat, importeMin, importeMax, irrMin,
  };
  const applyOcView = (f) => {
    setQ(f.q || "");
    setExpte(f.expte || "");
    setEstado(f.estado || "");
    setFaseAdj(f.faseAdj || "");
    setFechaPresDesde(f.fechaPresDesde || "");
    setFechaPresHasta(f.fechaPresHasta || "");
    if (f.direccionGeneral) setDireccionGeneral(f.direccionGeneral);
    setActividad(f.actividad || "all");
    setTipoContrat(Array.isArray(f.tipoContrat) ? f.tipoContrat : []);
    setImporteMin(f.importeMin || 0);
    setImporteMax(f.importeMax || 0);
    setIrrMin(f.irrMin || 0);
  };
  const resetOcFilters = () => applyOcView({});
  const ocChips = [];
  if (estado) ocChips.push({ key: "estado", label: `Estado: ${estado}`, onClear: () => setEstado("") });
  if (expte) ocChips.push({ key: "expte", label: `Expediente: ${expte}`, onClear: () => setExpte("") });
  if (faseAdj) ocChips.push({ key: "fase", label: faseAdj === "SIN_FASE" ? "Fase: Sin fase asignada" : (String(faseAdj).startsWith("TERM:") ? `Fase: ${faseAdj.slice(5)}` : `Fase: ${faseAdj}`), onClear: () => setFaseAdj("") });
  if (actividad && actividad !== "all") ocChips.push({ key: "actividad", label: `Actividad: ${actividad}`, onClear: () => setActividad("all") });
  if (direccionGeneral && direccionGeneral !== "all") ocChips.push({ key: "dg", label: `DG: ${direccionGeneral}`, onClear: () => setDireccionGeneral("all") });
  if (fechaPresDesde) ocChips.push({ key: "desde", label: `Desde: ${fechaPresDesde}`, onClear: () => setFechaPresDesde("") });
  if (fechaPresHasta) ocChips.push({ key: "hasta", label: `Hasta: ${fechaPresHasta}`, onClear: () => setFechaPresHasta("") });
  if (Array.isArray(tipoContrat) && tipoContrat.length) ocChips.push({ key: "tipo", label: `Tipo: ${tipoContrat.join("+")}`, onClear: () => setTipoContrat([]) });
  if (importeMin || importeMax) ocChips.push({ key: "importe", label: `Importe: ${importeMin || 0}-${importeMax || "∞"} M€`, onClear: () => { setImporteMin(0); setImporteMax(0); } });
  if (irrMin) ocChips.push({ key: "irr", label: `TIR ≥ ${irrMin}%`, onClear: () => setIrrMin(0) });

  // Iter184 · health-stats respeta filtro DG global (chip Servicios/Tratamiento)
  const _healthQS = () => {
    const p = new URLSearchParams();
    if (direccionGeneral && direccionGeneral !== "all") p.set("direccion_general", direccionGeneral);
    return p.toString() ? `?${p.toString()}` : "";
  };
  const reloadHealth = () =>
    axios.get(`${API}/ofertas-comercial/health-stats${_healthQS()}`)
      .then(r => setHealthStats(r.data))
      .catch(() => {});

  // Stats de salud del pipeline (banner superior) — se recarga cuando cambia el DG
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/ofertas-comercial/health-stats${_healthQS()}`)
      .then(r => { if (alive) setHealthStats(r.data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direccionGeneral]);

  // Iter184 · Auto-backfill de borradores incompletos al primer render.
  // Si hay borradores con cliente/presupuesto/fecha vacíos, dispara el endpoint
  // idempotente y recarga la lista + KPIs una sola vez por sesión.
  const [backfillDone, setBackfillDone] = useState(false);
  useEffect(() => {
    if (backfillDone || !healthStats) return;
    if ((healthStats.by_state?.BORRADOR ?? 0) === 0) { setBackfillDone(true); return; }
    let alive = true;
    axios.post(`${API}/ofertas-comercial/backfill-borradores`)
      .then(r => {
        if (!alive) return;
        setBackfillDone(true);
        if ((r.data?.filled ?? 0) > 0) {
          toast.success(`Borradores enriquecidos automáticamente (${r.data.filled} rellenados)`);
          reloadHealth();
        }
      })
      .catch(() => { setBackfillDone(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthStats, backfillDone]);

  // Merge suggestions (PLACSP↔Excel manual) — sólo cargamos el count para el banner
  const [mergeSuggestionsCount, setMergeSuggestionsCount] = useState(0);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  // Iter233 · Feedback usuario: "duplicados comerciales (mismo expediente)"
  // era de solo lectura ("no se puede actuar... para ver cuales y
  // depurarlo"). Ahora abre un modal accionable (fusionar). El botón de
  // "duplicados cross-source" (PLACSP+TED) se movió a Mercado — feedback
  // usuario: "el botón que salga solo en mercado".
  const [dupesComercialesOpen, setDupesComercialesOpen] = useState(false);
  const reloadMergeCount = useCallback(() => {
    axios.get(`${API}/ofertas-comercial/merge-suggestions?limit=1`)
      .then(r => setMergeSuggestionsCount(r.data.count || 0))
      .catch(() => setMergeSuggestionsCount(0));
  }, []);
  useEffect(() => { reloadMergeCount(); }, [reloadMergeCount]);
  // Columnas visibles persistidas en localStorage POR estado.
  // Si el estado tiene preset, lo aplicamos como default cuando el usuario
  // cambia de estado y aún no hay override manual guardado.
  const colsStorageKey = (est) => `oc_visible_cols_${est || "ALL"}`;
  const [visibleCols, setVisibleCols] = useState(() => {
    const knownKeys = new Set(ALL_COLS.map(c => c.key));
    const initial = COLS_POR_ESTADO[estado] || ALL_COLS.filter(c => c.default).map(c => c.key);
    try {
      const saved = JSON.parse(localStorage.getItem(colsStorageKey(estado)) || "null");
      if (Array.isArray(saved) && saved.length) {
        return saved.filter(k => knownKeys.has(k));
      }
    } catch {}
    return initial.filter(k => knownKeys.has(k));
  });
  // Cada vez que el usuario cambia de estado, intentamos cargar el preset
  // guardado, y si no existe, aplicamos el default de COLS_POR_ESTADO.
  useEffect(() => {
    const knownKeys = new Set(ALL_COLS.map(c => c.key));
    try {
      const saved = JSON.parse(localStorage.getItem(colsStorageKey(estado)) || "null");
      if (Array.isArray(saved) && saved.length) {
        setVisibleCols(saved.filter(k => knownKeys.has(k)));
        return;
      }
    } catch {}
    const preset = COLS_POR_ESTADO[estado] || ALL_COLS.filter(c => c.default).map(c => c.key);
    setVisibleCols(preset.filter(k => knownKeys.has(k)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);
  useEffect(() => {
    localStorage.setItem(colsStorageKey(estado), JSON.stringify(visibleCols));
  }, [visibleCols, estado]);
  const toggleCol = (key) => setVisibleCols(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  const sortFieldMap = {
    // Iter168 · Extendido para ordenar por todas las columnas típicas
    objeto: "objeto_contrato",
    cliente: "cliente",
    actividad: "actividad_comercial",
    tipo_contratacion: "tipo_contratacion",
    zona: "direccion_zona",
    ute: "empresa_g_valoriza",
    irr: "irr_manual",
    importe_ute: "presupuesto_licitacion_ute_mm",
    duracion: "duracion_anios",
    prorroga_col: "prorroga",
    mm: "presupuesto_licitacion_ute_mm",
    fecha_presentacion: "fecha_presentacion",
    fecha_apertura: "fecha_apertura",
    fecha_adjudicacion: "fecha_adjudicacion",
    estado: "estado_licitacion",
    fase_adj: "fase_adjudicacion",
    responsable: "responsables",
    ai_score: "ai_score",
    origen: "created_by",
  };
  const onSort = (colKey) => {
    const field = sortFieldMap[colKey];
    if (!field) return;
    if (sortBy === field) setSortDir(d => -d);
    else { setSortBy(field); setSortDir(-1); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (estado)  params.append("estado", estado);
      if (faseAdj && faseAdj !== "SIN_FASE" && !faseAdj.startsWith("TERM:")) params.append("fase_adjudicacion", faseAdj);
      if (direccionGeneral && direccionGeneral !== "all") params.append("direccion_general", direccionGeneral);
      if (actividad && actividad !== "all") params.append("actividad", actividad);
      if (tipoContrat.length > 0) params.append("tipo_contratacion_in", tipoContrat.join(","));
      if (q)       params.append("q", q);
      if (expte)   params.append("expediente", expte);
      if (mineOnly && user?.email) params.append("mine", "1");
      params.append("sort_by", sortBy);
      params.append("sort_dir", sortDir);
      // Iter201 · En Licitaciones sólo listamos SUSPENDIDA/ANULADA/DESISTIDA
      // que hayan pasado por PRESENTADA. Los casos pre-presentación viven
      // en Mercado.
      params.append("only_post_pres", "true");
      const r = await axios.get(`${API}/ofertas-comercial?${params.toString()}`);
      setItems(r.data || []);
      setLoadError(false);
    } catch (e) {
      // RCA · un fallo de carga NO debe quedar como "0 ofertas" silencioso:
      // mantenemos el último listado y avisamos para que el usuario reintente.
      setLoadError(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado, faseAdj, direccionGeneral, actividad, tipoContratKey, q, expte, sortBy, sortDir, mineOnly]);

  // Equipo para asignación rápida de responsable (listado).
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/autocomplete`)
      .then(r => setEquipo(r.data?.equipo?.length ? r.data.equipo : (r.data?.responsables || [])))
      .catch(() => {});
  }, []);
  const quickSetResponsable = async (o, val) => {
    try {
      await axios.patch(`${API}/ofertas-comercial/${o.id}/responsable`, { responsables: val });
      setItems(prev => prev.map(x => x.id === o.id ? { ...x, responsables: val } : x));
      toast.success(val ? "Responsable asignado" : "Responsable quitado");
    } catch { toast.error("No se pudo asignar"); }
  };

  // Iter225 · Fetch paralelo para KPIs — omite estado y fase_adjudicacion para
  // que las cards muestren siempre el conteo global (respetando el resto de
  // filtros: DG, actividad, búsqueda, mine…). No cambia cuando el usuario
  // pincha en un KPI, así que sólo se recarga con los filtros de contexto.
  const loadKpiItems = async () => {
    try {
      const params = new URLSearchParams();
      if (direccionGeneral && direccionGeneral !== "all") params.append("direccion_general", direccionGeneral);
      if (actividad && actividad !== "all") params.append("actividad", actividad);
      if (tipoContrat.length > 0) params.append("tipo_contratacion_in", tipoContrat.join(","));
      if (q)       params.append("q", q);
      if (expte)   params.append("expediente", expte);
      if (mineOnly && user?.email) params.append("mine", "1");
      params.append("only_post_pres", "true");
      const r = await axios.get(`${API}/ofertas-comercial?${params.toString()}`);
      setKpiItems(r.data || []);
      setLoadError(false);
    } catch {
      // noop — mantiene el último snapshot en caso de fallo puntual
      setLoadError(true);
    }
  };
  useEffect(() => { loadKpiItems(); }, [direccionGeneral, actividad, tipoContratKey, q, expte, mineOnly]);

  // Iter411 · Alerta de Arrastre — ofertas de años anteriores resueltas este año.
  // Usa EXACTAMENTE la misma fuente que el Hit rate (kpis/ytd) para que lo
  // marcado en Licitaciones coincida con lo que explica la tarjeta de KPIs.
  useEffect(() => {
    const dgParam = direccionGeneral && direccionGeneral !== "all" ? `&direccion_general=${direccionGeneral}` : "";
    const yr = new Date().getFullYear();
    axios.get(`${API}/ofertas-comercial/kpis/ytd?year=${yr}${dgParam}`)
      .then(r => {
        const res = r.data?.resueltas_ytd || {};
        const ids = new Set();
        [...(res.firmadas_items || []), ...(res.no_adjudicadas_items || [])]
          .filter(it => it.arrastre && it.id)
          .forEach(it => ids.add(it.id));
        setArrastreIds(ids);
      })
      .catch(() => setArrastreIds(new Set()));
  }, [direccionGeneral]);

  // Cuando se cambia DG → resetear actividad si ya no encaja
  useEffect(() => {
    if (direccionGeneral === "all") return;
    const allowed = ACTIVIDADES_POR_DG[direccionGeneral] || [];
    if (actividad !== "all" && !allowed.includes(actividad)) setActividad("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direccionGeneral]);

  const onSaved = () => { setEditing(null); load(); loadKpiItems(); };

  // Iter231/UX03 · Deep-link "?open=<id>": abre la ficha de esa oferta aunque
  // NO esté en la lista filtrada actual (p.ej. una decisión "por decidir" del
  // Cockpit vive en estados Mercado que Licitaciones excluye). Si no está en
  // `items`, se trae por id.
  useEffect(() => {
    const oid = new URLSearchParams(window.location.search).get("open");
    if (!oid) return;
    const clearParam = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("open");
      window.history.replaceState({}, "", url.toString());
    };
    const inList = items?.find((x) => x.id === oid);
    if (inList) { setEditing(inList); clearParam(); return; }
    let cancelled = false;
    axios.get(`${API}/ofertas-comercial/by-id/${oid}`)
      .then(({ data }) => { if (!cancelled && data) { setEditing(data); clearParam(); } })
      .catch(() => { /* no-op: id inexistente */ });
    return () => { cancelled = true; };
  }, [items]);

  // ════════════════════════════════════════════════════════════════════
  // Iter311 · G03 — Tablas y filtros persistentes: el estado completo de
  // filtros/orden se refleja en la URL (query string) para que al volver
  // de una ficha (navegación completa, no sólo cerrar el modal) o al
  // compartir el enlace, se conserven filtros y orden exactamente igual.
  // Usa replaceState (no "push") para no ensuciar el historial con cada tecla.
  // ════════════════════════════════════════════════════════════════════
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const setOrDel = (key, val) => { if (val) url.searchParams.set(key, val); else url.searchParams.delete(key); };
      setOrDel("estado", estado);
      setOrDel("fase", faseAdj);
      setOrDel("desde", fechaPresDesde);
      setOrDel("hasta", fechaPresHasta);
      setOrDel("actividad", actividad !== "all" ? actividad : "");
      setOrDel("tipo", tipoContratKey);
      setOrDel("q", q);
      setOrDel("expte", expte);
      setOrDel("sortBy", sortBy !== "fecha_presentacion" ? sortBy : "");
      setOrDel("sortDir", sortDir === 1 ? "1" : "");
      window.history.replaceState({}, "", url.toString());
    } catch { /* no-op */ }
  }, [estado, faseAdj, fechaPresDesde, fechaPresHasta, actividad, tipoContratKey, q, expte, sortBy, sortDir]);

  const del = async (o) => {
    const label = o.cliente ? `"${o.cliente}"${o.expediente ? ` (exp. ${o.expediente})` : ""}` : "esta oferta";
    if (!window.confirm(`¿Enviar ${label} a la papelera?\n\nNo se borra definitivamente: podrás restaurarla desde la papelera.`)) return;
    try {
      await axios.delete(`${API}/ofertas-comercial/${o.id}`);
      toast.success("Movida a la papelera — se puede restaurar");
      load(); loadKpiItems();
    } catch { toast.error("No se pudo eliminar"); }
  };
  const publish = async (oid, target_estado = "ESTUDIO") => {
    setPublishing({ id: oid, target_estado });
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/publish`, { target_estado });
      toast.success(`Oferta publicada en ${target_estado}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error publicando");
    } finally { setPublishing(null); }
  };

  const draftCount = useMemo(() => items.filter(o => o.is_draft || o.estado_licitacion === "BORRADOR").length, [items]);

  // Filtro IRR: solo aplica si irrMin > 0 (se usa computeIRR del módulo)
  // Compute ESTUDIO expired (VENCIDAS) - shown only when browsing ESTUDIO
  // or in the top banner as a global alert.
  const estudioVencidas = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return items.filter(o =>
      o.estado_licitacion === "ESTUDIO" &&
      o.fecha_presentacion &&
      o.fecha_presentacion < today
    );
  }, [items]);

  const runAutoCloseEstudio = async () => {
    // Iter303 · C03/E03 — ya no cierra directamente a PRESENTADA. Marca las
    // ofertas vencidas para revisión y abre el modal de confirmación con
    // evidencia (justificante / ampliación de plazo / NO PRESENTADA + motivo).
    try {
      await axios.post(`${API}/ofertas-comercial/admin/run-auto-close-estudio`);
      setShowConfirmacionModal(true);
      reloadHealth();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  // Iter168 · Toggle "Todas / Solo activas" · activas = presentadas del año
  // actual + vivas de años anteriores (fase_adjudicacion sin cerrar).
  const [showActiveOnly, setShowActiveOnly] = useState(() => {
    try {
      // Deep-link desde MERCADO a un estado de pipeline (sin fecha de
      // presentación): «Solo activas» los ocultaría, así que arranca en "Todas".
      const e = new URLSearchParams(window.location.search).get("estado");
      if (e && ["PUBLICADA", "POR DECIDIR", "ESTUDIO", "NO PRESENTADA", "BORRADOR"].includes(e)) return false;
      return localStorage.getItem("oc_show_active_only") !== "false";
    }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_show_active_only", String(showActiveOnly)); } catch {}
  }, [showActiveOnly]);
  const FASES_CERRADAS = useMemo(() => ["ADJUDICADA", "NO_ADJUDICADA", "FIRMADA", "RECURSO"], []);
  // Fases/estados RESUELTOS: son resultados históricos que el usuario consulta
  // explícitamente por su tarjeta. "Solo activas" NO debe ocultarlos aunque sean
  // de años previos (feedback David: "pincho Adjudicadas y no sale Barakaldo 2025").
  const RESOLVED_FASES = useMemo(() => ["ADJUDICADA", "ADJUDICADA_OTROS", "NO_ADJUDICADA", "FIRMADA"], []);
  const TERMINAL_ESTADOS = useMemo(() => ["SUSPENDIDA", "ANULADA", "DESISTIDA"], []);
  const isHistoricalSelection = RESOLVED_FASES.includes(faseAdj) || TERMINAL_ESTADOS.includes(estado) || String(faseAdj).startsWith("TERM:");

  // CC07 · Vistas rápidas sobre la cartera (predicados cliente, claros y citables).
  const [viewPreset, setViewPreset] = useState("todas");
  // Iter411 · Filtro rápido "Arrastre": ofertas de años previos resueltas este
  // año (las que marca el badge). Al activarse ignora "Solo activas" para que
  // los resultados históricos sean visibles.
  const [arrastreOnly, setArrastreOnly] = useState(false);

  const filteredItems = useMemo(() => {
    let base = items;
    if (highlightId) {
      base = base.filter(o => o.id === highlightId);
    }
    // #11 · tarjeta "Sin fase asignada" clicable → filtro cliente (no hay
    // filtro backend para "sin fase"): PRESENTADA cuya fase no es ninguna de
    // las reconocidas. El sentinel "SIN_FASE" no se envía al backend.
    const FASES_RECONOCIDAS = ["PTE_APERTURA","CLASIFICADOS_1","PTE_ADJUDICAR_OTROS","ADJUDICADA","ADJUDICADA_OTROS","NO_ADJUDICADA","FIRMADA","ANULADA","SUSPENDIDA","DESISTIDA"];
    if (faseAdj === "SIN_FASE") {
      base = base.filter(o => o.estado_licitacion === "PRESENTADA" && !FASES_RECONOCIDAS.includes(o.fase_adjudicacion));
    }
    // iter410 · Sentinel terminal "TERM:<FASE>" → estado terminal O fase terminal
    // (p.ej. San Roque: estado=PRESENTADA + fase=ANULADA aparece al pulsar Anulada).
    if (String(faseAdj).startsWith("TERM:")) {
      const term = faseAdj.slice(5);
      base = base.filter(o => o.estado_licitacion === term || o.fase_adjudicacion === term);
    }
    // Iter192 · Filtro rango de fecha de presentación (feedback usuario)
    if (fechaPresDesde || fechaPresHasta) {
      base = base.filter(o => {
        const fp = String(o.fecha_presentacion || "").slice(0, 10);
        if (fechaPresDesde && fp < fechaPresDesde) return false;
        if (fechaPresHasta && fp > fechaPresHasta) return false;
        return true;
      });
    }
    // Iter168/171/192 · Filtro año actual + vivas. Ahora que Borradores/
    // Publicadas/Por decidir/En estudio están sólo en Mercado, "vivas" en
    // Licitaciones son las presentadas del año o carryover con fase no
    // cerrada.
    // Iter399 · al BUSCAR (q) no aplicamos "Solo activas": la búsqueda debe
    // encontrar la oportunidad de la Agenda aunque sea de otro estado/año.
    // iter401 · Tampoco al drillear a una fase/estado RESUELTO (Adjudicadas,
    // No adjudicado, Firmadas, Adj. otros, Suspendida/Anulada/Desistida): son
    // resultados históricos; ocultarlos por año dejaba fuera adjudicadas de
    // años previos (Barakaldo 2025).
    if (showActiveOnly && !q && !isHistoricalSelection && !arrastreOnly) {
      const yearNow = String(new Date().getFullYear());
      base = base.filter(o => {
        const fp = (o.fecha_presentacion || "").slice(0, 4);
        const inYear = fp === yearNow;
        const alive = fp && fp < yearNow && !FASES_CERRADAS.includes(o.fase_adjudicacion);
        return inYear || alive;
      });
    }
    // Iter411 · Solo arrastre: ofertas marcadas como arrastre (resueltas este año,
    // presentadas en años previos). Misma fuente que el Hit rate.
    if (arrastreOnly) {
      base = base.filter(o => arrastreIds.has(o.id));
    }
    if (irrMin) {
      base = base.filter(o => {
        const { value } = computeIRR(o);
        return value != null && Number(value) >= irrMin;
      });
    }
    if (importeMin > 0 || importeMax > 0) {
      base = base.filter(o => {
        const mm = Number(o.presupuesto_licitacion_ute_mm || 0);
        if (importeMin > 0 && mm < importeMin) return false;
        if (importeMax > 0 && mm > importeMax) return false;
        return true;
      });
    }
    // CC07 · Vista rápida seleccionada
    if (viewPreset && viewPreset !== "todas") {
      const today = new Date().toISOString().slice(0, 10);
      const plus14 = new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
      base = base.filter(o => {
        if (viewPreset === "sin_asignar") return !String(o.responsables || "").trim();
        if (viewPreset === "vencimientos") {
          const fp = String(o.fecha_presentacion || "").slice(0, 10);
          return fp && fp >= today && fp <= plus14;
        }
        if (viewPreset === "pendiente_decision")
          return o.estado_licitacion === "PRESENTADA" && !FASES_CERRADAS.includes(o.fase_adjudicacion);
        if (viewPreset === "falta_info")
          return !String(o.zona || "").trim() || !String(o.responsables || "").trim() || !String(o.incumbent || "").trim();
        return true;
      });
    }
    return base;
  }, [items, irrMin, importeMin, importeMax, highlightId, showActiveOnly, q, fechaPresDesde, fechaPresHasta, FASES_CERRADAS, viewPreset, faseAdj, isHistoricalSelection, arrastreOnly, arrastreIds]);

  // #10 · Los contadores deben respetar el MISMO ámbito que la lista (Solo
  // activas + fechas + IRR + importe) para que no vuelva a pasar "la tarjeta
  // dice 5 y al pinchar salen 3". No aplica estado/fase (eso ES la tarjeta)
  // ni la vista rápida puntual.
  const kpiScoped = useMemo(() => {
    let base = kpiItems;
    if (fechaPresDesde || fechaPresHasta) {
      base = base.filter(o => {
        const fp = String(o.fecha_presentacion || "").slice(0, 10);
        if (fechaPresDesde && fp < fechaPresDesde) return false;
        if (fechaPresHasta && fp > fechaPresHasta) return false;
        return true;
      });
    }
    if (showActiveOnly && !q) {
      const yearNow = String(new Date().getFullYear());
      base = base.filter(o => {
        const fp = (o.fecha_presentacion || "").slice(0, 4);
        const inYear = fp === yearNow;
        const alive = fp && fp < yearNow && !FASES_CERRADAS.includes(o.fase_adjudicacion);
        return inYear || alive;
      });
    }
    if (irrMin) {
      base = base.filter(o => { const { value } = computeIRR(o); return value != null && Number(value) >= irrMin; });
    }
    if (importeMin > 0 || importeMax > 0) {
      base = base.filter(o => {
        const mm = Number(o.presupuesto_licitacion_ute_mm || 0);
        if (importeMin > 0 && mm < importeMin) return false;
        if (importeMax > 0 && mm > importeMax) return false;
        return true;
      });
    }
    return base;
  }, [kpiItems, showActiveOnly, q, fechaPresDesde, fechaPresHasta, irrMin, importeMin, importeMax, FASES_CERRADAS]);

  // iter401 · Igual que kpiScoped pero SIN "Solo activas". Las tarjetas de
  // fases/estados RESUELTOS cuentan TODO (incl. años previos) para que la cifra
  // de la tarjeta coincida con la lista al pinchar (principio "la tarjeta dice X
  // y al pinchar salen X").
  const kpiScopedAll = useMemo(() => {
    let base = kpiItems;
    if (fechaPresDesde || fechaPresHasta) {
      base = base.filter(o => {
        const fp = String(o.fecha_presentacion || "").slice(0, 10);
        if (fechaPresDesde && fp < fechaPresDesde) return false;
        if (fechaPresHasta && fp > fechaPresHasta) return false;
        return true;
      });
    }
    if (irrMin) {
      base = base.filter(o => { const { value } = computeIRR(o); return value != null && Number(value) >= irrMin; });
    }
    if (importeMin > 0 || importeMax > 0) {
      base = base.filter(o => {
        const mm = Number(o.presupuesto_licitacion_ute_mm || 0);
        if (importeMin > 0 && mm < importeMin) return false;
        if (importeMax > 0 && mm > importeMax) return false;
        return true;
      });
    }
    return base;
  }, [kpiItems, fechaPresDesde, fechaPresHasta, irrMin, importeMin, importeMax]);

  // Helpers de render por columna
  // Clasifica el origen del registro a partir de `created_by`
  const getOrigen = (o) => {
    const by = String(o.created_by || "").toLowerCase();
    if (by === "excel_import" || by.startsWith("seed_")) {
      return { kind: "excel",   label: "Excel maestro",       color: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: FileSpreadsheet };
    }
    if (by === "auto-import@system" || by.startsWith("placsp") || by.startsWith("boe") || by.startsWith("ted")) {
      return { kind: "placsp",  label: "Auto-import PLACSP / BOE / TED", color: "text-sky-700 bg-sky-50 border-sky-200", Icon: Globe };
    }
    if (by.includes("@")) {
      return { kind: "manual",  label: `Manual · ${o.created_by}`, color: "text-amber-700 bg-amber-50 border-amber-200", Icon: Pencil };
    }
    return { kind: "unknown",   label: "Origen desconocido",  color: "text-gray-500 bg-gray-50 border-gray-200", Icon: AlertTriangle };
  };

  // Iter303 · D09/E08 — solo tratamos como enlace real una URL http(s)
  // absoluta; cualquier otro valor (texto/descripción pegada por error) se
  // muestra como fuente no verificada en vez de un enlace potencialmente roto.
  const isValidLink = (link) => {
    if (!link) return false;
    try {
      const u = new URL(link);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch { return false; }
  };

  const renderCell = (o, col) => {
    switch (col.key) {
      case "objeto": return (
        <div className="leading-tight">
          {isValidLink(o.link) ? (
            <a
              href={o.link}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`Abrir pliego/anuncio original\n${o.objeto_contrato || ""}`}
              data-testid={`oc-link-${o.id}`}
              className="font-semibold text-[#007934] text-xs line-clamp-2 hover:underline decoration-dotted"
            >
              {o.objeto_contrato || o.cliente || "—"}
              <ExternalLink size={9} className="inline ml-1 -translate-y-px opacity-70"/>
            </a>
          ) : (
            <p className="font-semibold text-gray-900 text-xs line-clamp-2" title={o.link ? "Enlace guardado no es una URL válida — fuente no verificada" : o.objeto_contrato}>
              {o.objeto_contrato || o.cliente || "—"}
              {o.link && <span className="ml-1 text-[9px] text-amber-600 font-normal align-middle" data-testid={`oc-link-invalid-${o.id}`}>(fuente no verificada)</span>}
            </p>
          )}
          <p className="text-[10px] text-gray-400 font-mono truncate" title={o.expediente}>
            {o.expediente || "—"}
          </p>
          {arrastreIds.has(o.id) && (
            <span
              className="inline-flex items-center gap-1 mt-0.5 bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 text-[9px] font-semibold"
              title={`Arrastre: presentada antes de ${new Date().getFullYear()} y resuelta este año — cuenta en el Hit rate ${new Date().getFullYear()}`}
              data-testid={`oc-arrastre-badge-${o.id}`}
            >
              <History size={9}/> Arrastre {new Date().getFullYear()}
            </span>
          )}
        </div>
      );
      case "origen": {
        const { label, color, Icon } = getOrigen(o);
        return (
          <span
            className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${color}`}
            title={label}
            data-testid={`oc-origen-${o.id}-${getOrigen(o).kind}`}
          >
            <Icon size={11}/>
          </span>
        );
      }
      case "cliente":  return (
        <span className="text-gray-700 line-clamp-1 inline-flex items-center gap-1" title={o.cliente}>
          {(o.needs_fecha_presentacion || (o.estado_licitacion === "ESTUDIO" && !o.fecha_presentacion)) && (
            <span title="Oferta en ESTUDIO sin fecha de presentación · revisar"
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold"
                  data-testid={`needs-fecha-${o.id}`}>!</span>
          )}
          {(o.merge_suggestions?.length > 0) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setEditing(o); }}
              title={`${o.merge_suggestions.length} posible(s) fusión(es) con PLACSP · abrir para revisar`}
              className="inline-flex items-center justify-center gap-0.5 h-4 min-w-4 px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold hover:bg-indigo-200 transition"
              data-testid={`row-merge-badge-${o.id}`}>
              <Link2 size={9}/>{o.merge_suggestions.length}
            </button>
          )}
          {o.cliente || "—"}
        </span>
      );
      case "actividad":return <span className="text-gray-700">{o.actividad_comercial || "—"}</span>;
      case "tipo_contratacion": {
        // Iter151 · Campo unificado `tipo_contratacion` (enum NC/R/ADC).
        const canon = (o.tipo_contratacion || "").toString().trim().toUpperCase();
        if (!canon) return "—";
        return (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
            canon === "R"   ? "bg-emerald-100 text-emerald-700" :
            canon === "NC"  ? "bg-blue-100 text-blue-700" :
            canon === "ADC" ? "bg-purple-100 text-purple-700" : "bg-gray-100"
          }`} title={`Tipo contratación: ${canon}`} data-testid={`oc-row-tipo-${o.id}`}>{canon}</span>
        );
      }
      case "zona":     return <span className="text-gray-500">{o.zona || o.direccion_zona || "—"}</span>;
      case "gerencia": return <span className="text-gray-500">{o.gerencia || "—"}</span>;
      case "ute": {
        // Iter180 · UTE unificada: usa `isUte`/`formatUteLabel` compartidos.
        // Muestra socios + % en TODAS las tablas (antes: solo "UTE" o "UTE 60%").
        if (!isUte(o)) return <span className="text-gray-400 text-[10px]">—</span>;
        const label = formatUteLabel(o);
        // Iter152 · Riesgo UTE: fuera del rango 30-70% VSM se considera exposición
        // relevante (baja = riesgo de control por el socio, alta = dependencia).
        const risk = computeUteRisk(o);
        const pctUte = Number(o.porcentaje_ute) || 0;
        const badgeCls = risk === "low"
          ? "bg-rose-100 text-rose-700 border border-rose-300"
          : risk === "high"
            ? "bg-orange-100 text-orange-700 border border-orange-300"
            : "bg-amber-100 text-amber-700";
        const riskTitle = risk === "low"
          ? `⚠ Baja participación VSM (${pctUte}%) · riesgo de control por el socio · ${label}`
          : risk === "high"
            ? `⚠ Alta participación VSM (${pctUte}%) · dependencia interna elevada · ${label}`
            : label;
        return (
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${badgeCls}`}
            title={riskTitle}
            data-testid={`oc-row-ute-${o.id}`}
            data-ute-risk={risk || "ok"}
          >
            {risk && <AlertTriangle size={10} className="shrink-0"/>}
            <span className="truncate max-w-[140px]">{label}</span>
          </span>
        );
      }
      case "irr": {
        const { value, auto } = computeIRR(o);
        if (value == null) return <span className="text-gray-400">—</span>;
        const v = Number(value);
        const cls = v >= 12 ? "text-emerald-700 font-bold"
                  : v >=  8 ? "text-emerald-600 font-semibold"
                  : v >=  5 ? "text-amber-600 font-semibold"
                  : v >=  0 ? "text-gray-700"
                  :           "text-rose-600 font-semibold";
        return (
          <span className={`font-mono ${cls} ${auto ? "italic" : ""}`}
                title={auto ? "IRR estimada automáticamente (no fijada manualmente)" : "IRR fijada manualmente"}
                data-testid={`oc-irr-${o.id}`}>
            {v.toFixed(1)}%{auto && <span className="ml-0.5 text-[8px] text-gray-400 align-top">auto</span>}
          </span>
        );
      }
      case "importe_ute": {
        // Iter177 · Muestra el presupuesto BASE (sin aplicar %UTE). Para
        // consultar el importe *ya aplicado* el %UTE está la columna "M€"
        // (que renderiza presupuesto_licitacion_ute_mm) o el hint en la ficha.
        const base = (o.presupuesto != null && o.presupuesto !== "")
          ? Number(o.presupuesto)
          : (o.presupuesto_licitacion_ute_mm != null && o.presupuesto_licitacion_ute_mm !== ""
             ? Number(o.presupuesto_licitacion_ute_mm) * 1_000_000
             : null);
        if (base == null || !isFinite(base)) return <span className="text-gray-400">—</span>;
        return (
          <span className="font-mono text-xs" title="Presupuesto total del contrato, sin aplicar % UTE">
            {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(base)}
          </span>
        );
      }
      case "prorroga_col": return (o.prorroga != null && o.prorroga !== "")
        ? <span className="font-mono text-gray-700">{o.prorroga}m</span>
        : <span className="text-gray-400">—</span>;
      case "duracion": {
        // V01 (doc completitud-inferencia-cierre): NO inventar un total
        // combinando prórroga como años (antes: anios*12 + prorroga*12 daba
        // "72m" para 4 años + prórroga 2). Mostramos el PLAZO INICIAL; la
        // prórroga va en su propia columna con su unidad tal como se importó.
        const meses = (o.duracion_anios || 0) * 12;
        const title = meses
          ? `Plazo inicial: ${o.duracion_anios} año(s) = ${meses} meses. La prórroga (si la hay) se muestra aparte en la columna «Prórroga», con su unidad tal como se importó — no se suma aquí para no inventar el plazo total.`
          : "Duración (plazo inicial) no informada";
        return <span className="font-mono text-gray-700" title={title}>{meses ? `${meses}m` : "—"}</span>;
      }
      case "mm":       return <span className="font-mono font-semibold">{o.presupuesto_licitacion_ute_mm?.toFixed(2) || "—"}</span>;
      case "fecha_presentacion": {
        const fp = o.fecha_presentacion || "";
        // Regla iter 124 (Task E): si estado=ESTUDIO y la fecha ya pasó, marca
        // como VENCIDA con badge rojo pulsante forzando la revisión (mover a
        // PRESENTADA / NO PRESENTADA). El cron auto-cierra, pero si por
        // cualquier motivo no lo hizo (offline, error, etc.) el usuario ve
        // el bloqueante visual y puede accionar el pencil.
        const today = new Date().toISOString().slice(0, 10);
        const isEstudioVencida = fp && fp < today && o.estado_licitacion === "ESTUDIO";
        return (
          <span className={`inline-flex items-center gap-1 ${isEstudioVencida ? "font-bold text-rose-700" : "text-gray-500"}`}>
            {fmtFechaOferta(fp)}{o.hora_limite_presentacion ? <span className="text-gray-400 font-normal ml-0.5">{o.hora_limite_presentacion}</span> : null}
            {isEstudioVencida && (
              <span
                title={`Fecha de presentación vencida (${fmtFechaOferta(fp)}). Mueve la oferta a PRESENTADA o NO PRESENTADA.`}
                data-testid={`estudio-vencida-${o.id}`}
                className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white animate-pulse">
                <AlertTriangle size={9}/> VENCIDA
              </span>
            )}
          </span>
        );
      }
      case "fecha_apertura":     return <span className="text-gray-500">{fmtFechaOferta(o.fecha_apertura)}</span>;
      case "fecha_adjudicacion": return <span className="text-gray-500">{fmtFechaOferta(o.fecha_adjudicacion)}</span>;
      case "estado":   return o.estado_licitacion ? (
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${ESTADO_CLS[o.estado_licitacion] || "bg-gray-100"}`}>
          {o.estado_licitacion === "BORRADOR" && <Sparkles size={10} />}
          {o.estado_licitacion}
        </span>
      ) : "—";
      case "fase_adj": return o.fase_adjudicacion ? (
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${FASE_CLS[o.fase_adjudicacion] || "bg-gray-100"}`}>
          {o.fase_adjudicacion}
        </span>
      ) : "—";
      case "responsable": return <ResponsablePicker value={o.responsables} equipo={equipo} onChange={(val) => quickSetResponsable(o, val)} variant="cell" testid={`responsable-cell-${o.id}`} />;
      case "ai_score":    return o.ai_score != null ? (
        <span className={`inline-block w-7 h-7 rounded text-[10px] font-bold leading-7 text-center ${
          o.ai_score >= 85 ? "bg-green-100 text-green-700" :
          o.ai_score >= 70 ? "bg-emerald-100 text-emerald-700" :
          o.ai_score >= 50 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
        }`}>{o.ai_score}</span>
      ) : "—";
      default: return "—";
    }
  };

  const colsToRender = ALL_COLS.filter(c => visibleCols.includes(c.key));

  return (
    <div className="space-y-4">
      {loadError && (
        <div
          data-testid="oc-load-error-banner"
          className="flex items-center justify-between gap-3 border border-amber-300 bg-amber-50 rounded-lg px-4 py-2.5"
        >
          <span className="text-[13px] text-amber-800">
            No se pudieron cargar las licitaciones (problema puntual de conexión con la base de datos).
            Lo que ves puede estar incompleto — <b>no significa que haya 0 ofertas</b>. Reintenta en unos segundos.
          </span>
          <button
            type="button"
            onClick={() => { setLoadError(false); load(); loadKpiItems(); }}
            data-testid="oc-load-error-retry"
            className="shrink-0 text-[12px] font-semibold text-amber-900 border border-amber-400 rounded px-3 py-1 hover:bg-amber-100"
          >
            Reintentar
          </button>
        </div>
      )}
      {/* Iter214 · Panel KPI legacy (basado en healthStats) eliminado — duplicaba
          números con el "KPI vistoso" (basado en items filtrados) y generaba
          discrepancias. Ahora hay UN SOLO panel KPI (más abajo, "oc-quick-tabs-kpi")
          anclado a "Presentadas" y con todas las sub-cards derivadas del mismo
          array `items` filtrado. Total y sub-fases cuadran matemáticamente. */}

      {/* Iter212 · El breakdown "Pipeline: N ofertas · X borrador…" se ha
          retirado (redundante con las KPI cards y mezclaba estados que ya no
          pertenecen a Licitaciones — p.ej. NO PRESENTADAS viven en Mercado).
          Los warnings accionables (dups, motivos pendientes, estudio vencidas,
          transiciones auto) se conservan en un banner más compacto. */}
      {healthStats && (
        <div
          className="rounded-xl border border-gray-200 bg-white px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
          data-testid="oc-health-banner"
        >
          {healthStats.duplicados_comerciales_count > 0 && (
            <button
              type="button"
              onClick={() => setDupesComercialesOpen(true)}
              className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full hover:bg-rose-100 transition"
              data-testid="hs-dupes-comerciales"
              title="Ver y depurar duplicados (mismo expediente+cliente repetido)"
            >
              <AlertTriangle size={11}/>
              {healthStats.duplicados_comerciales_count} duplicados comerciales (mismo expediente)
            </button>
          )}
          {mergeSuggestionsCount > 0 && (
            <button
              onClick={() => setMergeModalOpen(true)}
              className="inline-flex items-center gap-1 text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full hover:bg-indigo-100 transition"
              data-testid="hs-merge-suggestions"
              title="Ofertas manuales que podrían coincidir con licitaciones PLACSP — revisar y vincular"
            >
              <Link2 size={11}/>
              {mergeSuggestionsCount} posibles fusiones
            </button>
          )}
          {healthStats.pending_motivo > 0 && (
            <button
              onClick={() => setShowMotivoModal(true)}
              data-testid="hs-pending-motivo"
              title="Ofertas auto-transicionadas a NO PRESENTADA que requieren que indiques el motivo"
              className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-300 px-2 py-0.5 rounded-full hover:bg-rose-100 font-semibold cursor-pointer">
              <AlertTriangle size={11}/>
              {healthStats.pending_motivo} pendiente{healthStats.pending_motivo === 1 ? "" : "s"} de motivo
            </button>
          )}
          {estudioVencidas.length > 0 && (
            <button
              onClick={runAutoCloseEstudio}
              data-testid="hs-estudio-vencidas"
              title="Ofertas en ESTUDIO con fecha de presentación pasada. Click para confirmar qué ocurrió (con evidencia) — ya no se cierran solas."
              className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 border border-amber-400 px-2 py-0.5 rounded-full hover:bg-amber-200 font-bold cursor-pointer">
              <AlertTriangle size={11}/>
              {estudioVencidas.length} ESTUDIO vencida{estudioVencidas.length === 1 ? "" : "s"} · confirmar
            </button>
          )}
          {healthStats.pending_confirmacion_presentacion > 0 && (
            <button
              onClick={() => setShowConfirmacionModal(true)}
              data-testid="hs-pending-confirmacion"
              title="Ofertas con plazo vencido esperando confirmación de presentación"
              className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full hover:bg-amber-100 font-semibold cursor-pointer">
              <AlertTriangle size={11}/>
              {healthStats.pending_confirmacion_presentacion} pendiente{healthStats.pending_confirmacion_presentacion === 1 ? "" : "s"} de confirmar presentación
            </button>
          )}
          <span
            className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
            data-testid="hs-auto"
            title="Ofertas auto-transicionadas (ESTUDIO→PRESENTADA / PUBLICADA·POR DECIDIR→NO PRESENTADA) en las últimas 24h"
          >
            <RefreshCw size={11}/>
            {healthStats.auto_transitions_24h} transici{healthStats.auto_transitions_24h === 1 ? "ón" : "ones"} auto / 24h
          </span>
          <span className="ml-auto text-[10px] text-gray-400">
            actualizado {new Date(healthStats.generated_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      )}

      {/* Iter214.1 · KPI vistoso — ÚNICO panel KPI de Licitaciones (reemplaza
          también los pills compactos que había antes). Anclado a "Presentadas"
          (tarjeta grande, resaltada). Todas las sub-cards son fases o estados
          post-presentación y se derivan del MISMO array `items`, por lo que
          los números cuadran perfectamente. Click aplica el filtro equivalente.
          Iter225 · Los contadores usan `kpiItems` (fetch sin estado/faseAdj)
          para que sigan mostrando el número real cuando el usuario tiene un
          KPI activo — sólo pierden saturación visualmente. */}
      <div className="space-y-2">
        {/* Fila 1 · Anclas (Todas + Presentadas) */}
        <div className="grid grid-cols-2 gap-2" data-testid="oc-kpi-anchors">
          {(() => {
            const fmtM = (n) => `${(Number(n)/1_000_000).toFixed(2)} M€`;
            const anyKpiActive = !!(estado || faseAdj);
            const anchors = [
              { key: "all",         label: "Todas",       color: "from-slate-50 to-slate-100 text-slate-900 border-slate-300",  match: () => true },
              { key: "presentadas", label: "Presentadas", color: "from-blue-100 to-blue-200 text-blue-900 border-blue-300",     match: o => o.estado_licitacion === "PRESENTADA" },
            ];
            const vistaByKey = Object.fromEntries(VISTAS_PREDEFINIDAS.map(v => [v.key, v]));
            return anchors.map(k => {
              const subset = kpiScoped.filter(k.match);
              const count = subset.length;
              const importe = subset.reduce((s, o) => s + ((o.presupuesto_licitacion_ute_mm || 0) * 1_000_000), 0);
              const v = vistaByKey[k.key];
              const active = v && estado === v.estado && faseAdj === v.faseAdj;
              const dimmed = anyKpiActive && !active;
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => { if (v) { setEstado(v.estado); setFaseAdj(v.faseAdj); } }}
                  data-testid={`oc-kpi-vista-${k.key}`}
                  className={`text-left px-4 py-3 rounded-xl border-2 bg-gradient-to-br transition-all hover:shadow-md hover:scale-[1.01] shadow-sm ${k.color} ${
                    active ? "ring-2 ring-offset-1 ring-[#007934] shadow-md" : ""
                  } ${dimmed ? "opacity-45 hover:opacity-90" : ""}`}
                  title={`Ver ${k.label.toLowerCase()} · ${count} ofertas · ${fmtM(importe)}`}>
                  <div className="text-[11px] uppercase tracking-wide font-semibold opacity-75">{k.label}</div>
                  <div className="text-3xl font-bold font-['Outfit'] leading-tight tabular-nums" data-testid={`oc-kpi-value-${k.key}`}>
                    {loading ? <span className="inline-block h-7 w-10 bg-black/10 rounded animate-pulse"/> : count}
                  </div>
                  <div className="text-[12px] font-mono opacity-80 mt-0.5">{loading ? "…" : fmtM(importe)}</div>
                </button>
              );
            });
          })()}
        </div>
        {/* Fila 2 · Fases de Presentadas + terminales post-pres */}
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-2" data-testid="oc-quick-tabs-kpi">
          {(() => {
            const fmtM = (n) => `${(Number(n)/1_000_000).toFixed(2)} M€`;
            const anyKpiActive = !!(estado || faseAdj);
            const kpiVistas = [
              // Fases de Presentadas
              { key: "apertura",      label: "Pte. Apertura",  color: "from-cyan-50 to-cyan-100 text-cyan-800 border-cyan-200",         match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "PTE_APERTURA" },
              { key: "clasif1",       label: "Clasif. 1",      color: "from-teal-50 to-teal-100 text-teal-800 border-teal-200",         match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "CLASIFICADOS_1" },
              { key: "pte_adj_otros", label: "Pte. Adj. otros",color: "from-sky-50 to-sky-100 text-sky-800 border-sky-200",             match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "PTE_ADJUDICAR_OTROS" },
              { key: "adjudicadas",   label: "Adjudicadas",    color: "from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200", match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "ADJUDICADA" },
              { key: "adj_otros",     label: "Adj. otros",     color: "from-lime-50 to-lime-100 text-lime-800 border-lime-200",         match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "ADJUDICADA_OTROS" },
              { key: "no_adjudicado", label: "No adjudicado",  color: "from-rose-50 to-rose-100 text-rose-800 border-rose-200",         match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "NO_ADJUDICADA" },
              { key: "firmadas",      label: "Firmadas",       color: "from-green-50 to-green-100 text-green-800 border-green-200",     match: o => o.estado_licitacion === "PRESENTADA" && o.fase_adjudicacion === "FIRMADA" },
              // Iter230 · Feedback usuario "No suman": PRESENTADA sin fase_adjudicacion
              // reconocida (recién presentada, aún sin clasificar) no encajaba en
              // ninguna de las 7 fases de arriba → el desglose no sumaba el total
              // de "Presentadas". Esta card cierra el hueco.
              { key: "sin_fase",       label: "Sin fase asignada", color: "from-slate-100 to-slate-200 text-slate-700 border-slate-300", match: o => o.estado_licitacion === "PRESENTADA" && !["PTE_APERTURA","CLASIFICADOS_1","PTE_ADJUDICAR_OTROS","ADJUDICADA","ADJUDICADA_OTROS","NO_ADJUDICADA","FIRMADA","ANULADA","SUSPENDIDA","DESISTIDA"].includes(o.fase_adjudicacion) },
              // Terminales post-pres · iter410: cuentan por ESTADO terminal
              // (pre-pres) O por FASE terminal (presentada y luego anulada/
              // suspendida/desistida en adjudicación, p.ej. San Roque:
              // estado=PRESENTADA + fase=ANULADA → debe caer aquí, no en "Sin fase").
              { key: "suspendida",    label: "Suspendida",     color: "from-amber-50 to-amber-100 text-amber-800 border-amber-200",     match: o => o.estado_licitacion === "SUSPENDIDA" || o.fase_adjudicacion === "SUSPENDIDA" },
              { key: "anulada",       label: "Anulada",        color: "from-red-50 to-red-100 text-red-800 border-red-200",             match: o => o.estado_licitacion === "ANULADA" || o.fase_adjudicacion === "ANULADA" },
              { key: "desistida",     label: "Desistida",      color: "from-orange-50 to-orange-100 text-orange-800 border-orange-200", match: o => o.estado_licitacion === "DESISTIDA" || o.fase_adjudicacion === "DESISTIDA" },
            ];
            const vistaByKey = Object.fromEntries(VISTAS_PREDEFINIDAS.map(v => [v.key, v]));
            // iter401 · Tarjetas de resultados históricos: cuentan desde
            // kpiScopedAll (sin "Solo activas") para cuadrar con la lista.
            const HISTORICAL_CHIP_KEYS = new Set(["adjudicadas", "adj_otros", "no_adjudicado", "firmadas", "suspendida", "anulada", "desistida"]);
            // iter410 · Las terminales usan un sentinel cliente "TERM:<FASE>"
            // (no filtro backend) para capturar tanto estado=X como fase=X.
            const TERMINAL_CHIP = { suspendida: "SUSPENDIDA", anulada: "ANULADA", desistida: "DESISTIDA" };
            return kpiVistas.map(k => {
              const subset = (HISTORICAL_CHIP_KEYS.has(k.key) ? kpiScopedAll : kpiScoped).filter(k.match);
              const count = subset.length;
              const importe = subset.reduce((s, o) => s + ((o.presupuesto_licitacion_ute_mm || 0) * 1_000_000), 0);
              const v = vistaByKey[k.key];
              const isTerminal = !!TERMINAL_CHIP[k.key];
              const active = k.key === "sin_fase"
                ? (estado === "PRESENTADA" && faseAdj === "SIN_FASE")
                : isTerminal
                  ? (faseAdj === `TERM:${TERMINAL_CHIP[k.key]}`)
                  : (v && estado === v.estado && faseAdj === v.faseAdj);
              const dimmed = anyKpiActive && !active;
              if (k.key === "sin_fase") {
                // #11 · clicable → filtro cliente "SIN_FASE" (PRESENTADA sin
                // fase reconocida). No hay filtro backend, se resuelve en
                // filteredItems con el sentinel.
                return (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => { setEstado("PRESENTADA"); setFaseAdj("SIN_FASE"); }}
                    data-testid={`oc-kpi-vista-${k.key}`}
                    title={`${count} ofertas presentadas sin fase de adjudicación asignada aún · ${fmtM(importe)} · pulsa para verlas`}
                    className={`text-left px-2.5 py-2 rounded-xl border-2 bg-gradient-to-br transition-all hover:shadow-md hover:scale-[1.02] ${k.color} ${
                      active ? "ring-2 ring-offset-1 ring-[#007934] shadow-md" : (count === 0 ? "opacity-60 hover:opacity-90" : "opacity-95 hover:opacity-100")
                    } ${dimmed ? "!opacity-45 hover:!opacity-90" : ""}`}>
                    <div className="text-[9px] uppercase tracking-wide font-semibold opacity-75 truncate">{k.label}</div>
                    <div className="text-xl font-bold font-['Outfit'] leading-tight tabular-nums">{loading ? <span className="inline-block h-5 w-6 bg-black/10 rounded animate-pulse"/> : count}</div>
                    <div className="text-[10px] font-mono opacity-80 mt-0.5 truncate">{loading ? "…" : fmtM(importe)}</div>
                  </button>
                );
              }
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => { if (isTerminal) { setEstado(""); setFaseAdj(`TERM:${TERMINAL_CHIP[k.key]}`); } else if (v) { setEstado(v.estado); setFaseAdj(v.faseAdj); } }}
                  data-testid={`oc-kpi-vista-${k.key}`}
                  className={`text-left px-2.5 py-2 rounded-xl border-2 bg-gradient-to-br transition-all hover:shadow-md hover:scale-[1.02] ${k.color} ${
                    active ? "ring-2 ring-offset-1 ring-[#007934] shadow-md" : (count === 0 ? "opacity-60 hover:opacity-90" : "opacity-95 hover:opacity-100")
                  } ${dimmed ? "!opacity-45 hover:!opacity-90" : ""}`}
                  title={`Ver ${k.label.toLowerCase()} · ${count} ofertas · ${fmtM(importe)}`}>
                  <div className="text-[9px] uppercase tracking-wide font-semibold opacity-75 truncate">{k.label}</div>
                  <div className="text-xl font-bold font-['Outfit'] leading-tight tabular-nums">{loading ? <span className="inline-block h-5 w-6 bg-black/10 rounded animate-pulse"/> : count}</div>
                  <div className="text-[10px] font-mono opacity-80 mt-0.5 truncate">{loading ? "…" : fmtM(importe)}</div>
                </button>
              );
            });
          })()}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1 gap-1">
          <Search size={12} className="text-gray-400"/>
          <input data-testid="oc-search" placeholder="Buscar (tolera tildes): concurso, cliente, expediente, lote…" value={q} onChange={e=>setQ(e.target.value)} className="text-xs outline-none w-56"/>
        </div>
        <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1 gap-1" title="Filtra por código de expediente (coincidencia parcial)">
          <span className="text-[10px] font-semibold text-gray-500 uppercase">Expte.</span>
          <input data-testid="oc-filter-expediente" placeholder="p.ej. 2026/123" value={expte} onChange={e=>setExpte(e.target.value)} className="text-xs outline-none w-28"/>
          {expte && (
            <button type="button" onClick={() => setExpte("")} className="text-gray-400 hover:text-rose-600" title="Limpiar expediente" data-testid="oc-filter-expediente-clear">×</button>
          )}
        </div>
        <SavedViewsBar screen="licitaciones" currentFilters={ocFilters} onApply={applyOcView} />
        {/* Iter179.1 · Foco por comercial */}
        <button
          type="button"
          onClick={() => setMineOnly(v => !v)}
          data-testid="oc-filter-mine"
          title={mineOnly ? "Mostrando solo las ofertas donde figuras como responsable. Click para ver todo." : "Mostrando todas. Click para ver solo las tuyas."}
          className={`text-xs font-semibold px-3 py-1 rounded-lg flex items-center gap-1 border transition ${
            mineOnly
              ? "bg-[#007934] border-[#007934] text-white hover:bg-[#006528]"
              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}>
          {mineOnly ? "Solo mías" : "Ver todo"}
        </button>
        <span className="inline-flex items-center gap-1">
          <select
            data-testid="oc-filter-actividad"
            value={actividad}
            onChange={e => setActividad(e.target.value)}
            title={direccionGeneral === "all" ? "Actividad (todas las DG)" : `Actividades de ${direccionGeneral}`}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
            <option value="all">Toda actividad</option>
            {(direccionGeneral === "all" ? ALL_ACTIVIDADES : (ACTIVIDADES_POR_DG[direccionGeneral] || []))
              .map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <HelpTip text="Actividad de negocio de la oferta (C&W, GA, MOB, Light Capex, PPP…). Si tienes un filtro de Dirección General activo arriba, aquí solo aparecen las actividades de esa DG."/>
        </span>
        <span className="inline-flex items-center gap-1">
          <select data-testid="oc-filter-estado" value={estado} onChange={e=>setEstado(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white" title="Estado. Vacío = todos los estados válidos de Licitaciones (PRESENTADA + terminales post-presentación).">
            <option value="">Todos (post-presentación)</option>
            {ESTADOS_OPCIONES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <HelpTip text="Estado de la licitación una vez presentada (Presentada y estados posteriores: adjudicada a nosotros / a otros, no adjudicada, firmada, suspendida, anulada, desistida). Vacío = todos los estados válidos post-presentación."/>
        </span>
        {/* Iter192 · Rango de fecha de presentación (feedback usuario) */}
        <div className="inline-flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1 text-[11px] bg-white" title="Filtrar por rango de fecha de presentación">
          <span className="text-gray-500 font-semibold">Pres.</span>
          <HelpTip text="Rango por fecha de presentación de la oferta (la fecha límite en que se entrega o se entregó). Puedes fijar solo «desde», solo «hasta», o ambas."/>
          <input type="date" value={fechaPresDesde} onChange={e => setFechaPresDesde(e.target.value)}
                 className="text-[11px] border-0 outline-none bg-transparent" data-testid="oc-filter-fecha-pres-desde"/>
          <span className="text-gray-400">→</span>
          <input type="date" value={fechaPresHasta} onChange={e => setFechaPresHasta(e.target.value)}
                 className="text-[11px] border-0 outline-none bg-transparent" data-testid="oc-filter-fecha-pres-hasta"/>
          {(fechaPresDesde || fechaPresHasta) && (
            <button type="button" onClick={() => { setFechaPresDesde(""); setFechaPresHasta(""); }}
                    className="text-gray-400 hover:text-rose-600 ml-1" title="Limpiar rango"
                    data-testid="oc-filter-fecha-pres-clear">×</button>
          )}
        </div>
        {/* Iter168/230 · Toggle "Todas / Solo activas" → dropdown claro (feedback usuario) */}
        <span className="inline-flex items-center gap-1">
          <select
            data-testid="oc-filter-active-only"
            value={showActiveOnly ? "activas" : "todas"}
            onChange={e => setShowActiveOnly(e.target.value === "activas")}
            title="Elige qué ofertas se muestran en la tabla"
            className={`text-xs px-2 py-1 rounded-lg border transition ${showActiveOnly ? "bg-emerald-100 border-emerald-300 text-emerald-800 font-semibold" : "bg-white border-gray-200 text-gray-500"}`}
          >
            <option value="activas">Solo activas · {new Date().getFullYear()} + vivas de años anteriores</option>
            <option value="todas">Todas · histórico completo sin filtrar por año</option>
          </select>
          <HelpTip text="«Solo activas» = las presentadas de este año más las de años anteriores que siguen vivas (fase de adjudicación no cerrada). «Todas» = histórico completo, sin filtrar por año."/>
        </span>
        {arrastreIds.size > 0 && (
          <button
            type="button"
            data-testid="oc-filter-arrastre"
            onClick={() => setArrastreOnly(v => !v)}
            title={`Ofertas presentadas antes de ${new Date().getFullYear()} y resueltas este año — las que cuentan en el Hit rate ${new Date().getFullYear()}`}
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition ${arrastreOnly ? "bg-amber-500 border-amber-500 text-white font-semibold" : "bg-white border-amber-300 text-amber-700 hover:bg-amber-50"}`}
          >
            <History size={12}/> Arrastre {new Date().getFullYear()} · {arrastreIds.size}
          </button>
        )}
        <span className="inline-flex items-center gap-1">
          <select data-testid="oc-filter-fase" value={faseAdj} onChange={e=>setFaseAdj(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
            <option value="">Todas las fases adj.</option>
            {FASES_ADJ.map(f => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
          </select>
          <HelpTip text="Fase del proceso de adjudicación tras presentar la oferta (pendiente de apertura, clasificados, adjudicada a nosotros / a otros, no adjudicada, firmada…)."/>
        </span>
        {/* Iter154/230 · filtro por tipo de contratación — multi-select
            (feedback usuario: poder marcar NC+R a la vez) */}
        <div className="relative inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setTipoContratOpen(o => !o)}
            data-testid="oc-filter-tipo-contratacion"
            title="Tipo de contratación (multi-selección)"
            className={`border rounded-lg px-2 py-1 text-xs bg-white flex items-center gap-1.5 ${
              tipoContrat.length > 0 ? "border-[#007934] text-[#0a5d2f] font-semibold" : "border-gray-200 text-gray-700"
            }`}
          >
            {tipoContrat.length === 0 ? "Todo tipo" : `Tipo: ${tipoContrat.join("+")}`}
            <ChevronDown size={12}/>
          </button>
          <HelpTip text="Tipo de contratación: Renovación (R) = recontratamos un servicio que ya prestábamos; Nueva contratación (NC) = contrato nuevo para Valoriza; Adicional (ADC) = ampliación o complementario de uno existente. Puedes marcar varios a la vez."/>
          {tipoContratOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setTipoContratOpen(false)}/>
              <div className="absolute left-0 mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48" data-testid="oc-tipo-contratacion-popover">
                {[{ v: "R", label: "Renovación" }, { v: "NC", label: "Nueva contratación" }, { v: "ADC", label: "Adicional" }].map(opt => (
                  <label key={opt.v} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
                         data-testid={`oc-tipo-contratacion-opt-${opt.v}`}>
                    <input type="checkbox" checked={tipoContrat.includes(opt.v)} onChange={() => toggleTipoContrat(opt.v)} className="accent-[#007934]"/>
                    <span className="text-gray-700">{opt.label} <span className="text-gray-400">({opt.v})</span></span>
                  </label>
                ))}
                {tipoContrat.length > 0 && (
                  <div className="border-t border-gray-100 mt-1 px-3 py-1.5">
                    <button type="button" onClick={() => setTipoContrat([])} className="text-[10px] text-gray-400 hover:text-rose-600" data-testid="oc-tipo-contratacion-clear">
                      Limpiar selección
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {/* Rango de importe (M€, total de licitación con UTE) — pedido del usuario */}
        <ImporteRangeFilter
          min={importeMin}
          max={importeMax}
          onChange={(mn, mx) => { setImporteMin(mn); setImporteMax(mx); }}
          testidPrefix="oc-filter-importe"
        />
        {/* Slider IRR ≥ X% — filtra (computeIRR) en cliente */}
        <div
          data-testid="oc-filter-irr-wrap"
          title="Filtra ofertas con IRR (manual o auto) por encima del umbral"
          className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
            irrMin > 0 ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"
          }`}>
          <span className={irrMin > 0 ? "font-semibold text-amber-700" : "text-gray-500"}>
            IRR ≥
          </span>
          <HelpTip text="IRR = rentabilidad interna estimada de la oferta (manual o calculada). Filtra las ofertas con IRR igual o superior al umbral."/>
          <input
            type="range"
            min={0}
            max={30}
            step={1}
            value={irrMin}
            onChange={(e) => setIrrMin(Number(e.target.value))}
            data-testid="oc-filter-irr"
            className="accent-amber-500 w-24"
          />
          <span className={`font-mono w-9 text-right ${irrMin > 0 ? "text-amber-700 font-bold" : "text-gray-500"}`}>
            {irrMin}%
          </span>
          {irrMin > 0 && (
            <button
              onClick={() => setIrrMin(0)}
              data-testid="oc-filter-irr-reset"
              title="Quitar filtro IRR"
              className="text-amber-700 hover:text-amber-900">
              <X size={11}/>
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500" data-testid="oc-count">{loading ? "Cargando…" : `${filteredItems.length} ${filteredItems.length === 1 ? "licitación" : "licitaciones"} · ${q ? "búsqueda en todos los estados" : (isHistoricalSelection ? "incluye años previos" : (showActiveOnly ? "solo activas" : "histórico completo"))}${irrMin > 0 && filteredItems.length !== items.length ? ` (de ${items.length})` : ""}`}</span>
        {highlightId && (
          <a
            href="/ofertas-comercial"
            data-testid="oc-clear-highlight"
            className="text-[11px] px-2 py-1 rounded-full font-semibold flex items-center gap-1 border bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
            title="Ver todas las licitaciones (quitar destacado desde Mercado)">
            <X size={11}/> Ver todas
          </a>
        )}
        {draftCount > 0 && (
          <button
            onClick={() => setEstado(estado === "BORRADOR" ? "" : "BORRADOR")}
            data-testid="oc-drafts-chip"
            className={`text-[11px] px-2 py-1 rounded-full font-semibold flex items-center gap-1 transition-colors border ${
              estado === "BORRADOR"
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100"
            }`}
            title="Filtrar por oportunidades en BORRADOR (auto-importadas Pando)">
            <Sparkles size={11}/> {draftCount} en BORRADOR
          </button>
        )}
        <div className="flex-1"/>
        {/* Selector columnas */}
        <div className="relative">
          <button onClick={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); setColMenuAlign(r.left > window.innerWidth/2 ? "right" : "left"); setShowColMenu(s=>!s); }} data-testid="oc-cols-btn" className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex items-center gap-1 hover:bg-gray-50">
            <Settings size={11}/> Columnas ({visibleCols.length})
          </button>
          {showColMenu && (
            <div className={`absolute ${colMenuAlign === "right" ? "right-0" : "left-0"} mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 p-2 space-y-1 z-[60] max-h-80 overflow-y-auto`} data-testid="oc-cols-menu">
              {ALL_COLS.map(c => (
                <label key={c.key} className="flex items-center gap-2 text-xs text-gray-700 hover:bg-gray-50 px-1.5 py-1 rounded cursor-pointer">
                  <input type="checkbox" checked={visibleCols.includes(c.key)} onChange={()=>toggleCol(c.key)} className="accent-[#007934]" data-testid={`oc-col-${c.key}`}/>
                  {c.label}
                </label>
              ))}
              <button onClick={()=>setVisibleCols(ALL_COLS.filter(c=>c.default).map(c=>c.key))} className="text-[10px] text-gray-400 hover:text-gray-600 underline w-full text-center pt-1 border-t border-gray-100">
                restaurar default
              </button>
            </div>
          )}
        </div>
        {canDelete && (
          <button onClick={() => setShowPapelera(true)} data-testid="oc-papelera-btn"
            title="Papelera: registros eliminados (recuperables)"
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex items-center gap-1 hover:bg-gray-50 text-gray-600">
            <Trash2 size={11}/> Papelera
          </button>
        )}
        {/* Iter192 · Botón "Importar PLACSP/BOE/TED" retirado de Licitaciones.
            La importación de plataformas de contratación se realiza sólo
            desde MERCADO (donde cae como "Detectadas") según feedback usuario. */}
        {/* Iter185.1 · Botón "Nueva oferta" retirado del panel Licitaciones.
            Se crea exclusivamente desde MERCADO (fila mercado-new-oferta-{id})
            y desde el botón físico "Nueva oferta" del tab Mercado. */}
      </div>

      <ActiveFilterChips chips={ocChips} onClearAll={ocChips.length ? resetOcFilters : null} />


      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {estado === "BORRADOR" && (
          <div className="bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-start gap-2 text-[11px] text-orange-800" data-testid="oc-drafts-banner">
            <Sparkles size={13} className="text-orange-600 mt-0.5 flex-shrink-0"/>
            <div>
              <strong>Bandeja de borradores Pando.</strong> Estas oportunidades fueron auto-importadas por la Pando con score ≥ 95.
              Revisa cada una y pulsa <em>Publicar</em> para moverla a ESTUDIO. Si no es relevante, elimínala.
            </div>
          </div>
        )}
        {loading ? <div className="p-6 text-center"><Loader2 className="animate-spin inline mr-2" size={14}/> Cargando…</div> :
          <>
          <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="oc-views">
            <span className="text-[10px] uppercase text-gray-400 font-semibold mr-1">Vistas</span>
            <button type="button" onClick={() => setMineOnly(v => !v)} data-testid="oc-view-mias"
              title="Ofertas donde figuras como responsable"
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${mineOnly ? "bg-[#007934] text-white border-[#007934] font-semibold" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}>
              Mías
            </button>
            {[
              { k: "sin_asignar", l: "Sin asignar", help: "Sin responsable asignado" },
              { k: "vencimientos", l: "Próximos vencimientos", help: "Presentación en los próximos 14 días" },
              { k: "pendiente_decision", l: "Pendiente de resolución", help: "Presentadas aún sin resultado de adjudicación (fase no cerrada)" },
              { k: "falta_info", l: "Falta información", help: "Sin zona, sin responsable o sin incumbente" },
            ].map(v => (
              <button key={v.k} type="button" title={v.help} data-testid={`oc-view-${v.k}`}
                onClick={() => setViewPreset(viewPreset === v.k ? "todas" : v.k)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition ${viewPreset === v.k ? "bg-sky-600 text-white border-sky-600 font-semibold" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}>
                {v.l}
              </button>
            ))}
          </div>
          <FasesSinAsignarTray onOpen={(id) => { const it = items.find(x => x.id === id); if (it) setEditing(it); }}/>
          <CompletitudTray onOpen={(id) => { const it = items.find(x => x.id === id); if (it) setEditing(it); }} onBulk={() => setShowBulk(true)}/>
          <div className="overflow-x-auto table-sticky-scroll" data-testid="oc-table-scroll">
            <table className="text-xs" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
              <colgroup>
                {colsToRender.map(c => <col key={c.key} className={c.width || ""} />)}
                <col className="w-[60px]" />
              </colgroup>
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
                <tr>
                  {colsToRender.map(c => {
                    const sortable = sortFieldMap[c.key];
                    const active = sortable && sortBy === sortable;
                    return (
                      <th key={c.key} className={`p-2.5 align-bottom break-words leading-tight ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"} ${sortable ? "cursor-pointer hover:bg-gray-100 select-none" : ""}`}
                          onClick={() => sortable && onSort(c.key)}
                          data-testid={sortable ? `oc-th-sort-${c.key}` : undefined}
                          title={c.title || undefined}>
                        <span className={c.title ? "underline decoration-dotted cursor-help" : ""}>{c.label}</span>
                        {sortable && (active
                          ? <span className="ml-1 text-emerald-700">{sortDir === -1 ? "▼" : "▲"}</span>
                          : <span className="ml-1 text-gray-300">⇅</span>
                        )}
                      </th>
                    );
                  })}
                  <th className="text-center p-2.5 w-[60px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map(o => {
                  const isDraft = o.is_draft || o.estado_licitacion === "BORRADOR";
                  const isPublishing = publishing?.id === o.id;
                  const isHighlighted = highlightId && o.id === highlightId;
                  return (
                  <tr key={o.id}
                      ref={isHighlighted ? (el) => el?.scrollIntoView({ behavior: "smooth", block: "center" }) : undefined}
                      className={`hover:bg-gray-50 ${isDraft ? "bg-orange-50/40 border-l-2 border-l-orange-400" : ""} ${isHighlighted ? "bg-blue-50 ring-2 ring-blue-500 ring-inset shadow-lg" : ""}`}
                      data-testid={`oc-row-${o.id}`}>
                    {colsToRender.map(c => (
                      <td key={c.key} className={`p-2.5 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}`}>
                        {renderCell(o, c)}
                      </td>
                    ))}
                    <td className="p-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        {isDraft && (
                          <button
                            onClick={() => publish(o.id, "ESTUDIO")}
                            disabled={isPublishing}
                            data-testid={`oc-publish-${o.id}`}
                            title="Publicar a ESTUDIO (sale del borrador)"
                            className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1 disabled:opacity-50">
                            {isPublishing ? <Loader2 size={10} className="animate-spin"/> : <Send size={10}/>}
                            Publicar
                          </button>
                        )}
                        <button
                          onClick={() => setShowCompetitive(o)}
                          title="Análisis competitivo Pando (Claude + noticias scrapeadas)"
                          data-testid={`oc-competitive-${o.id}`}
                          className="text-amber-500 hover:text-amber-700 p-1 rounded hover:bg-amber-50">
                          <Bot size={12}/>
                        </button>
                        <button
                          onClick={() => setShowCompliance(o)}
                          title="Compliance Check Pando (plazos, presupuesto, encaje, bloqueantes)"
                          data-testid={`oc-compliance-${o.id}`}
                          className="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-50">
                          <ShieldCheck size={12}/>
                        </button>
                        <button
                          onClick={()=>setEditing(o)}
                          title="Modificar oferta (todos los estados)"
                          className="text-[#007934] hover:text-white hover:bg-[#007934] p-1.5 rounded-md border border-[#007934]/40 hover:border-[#007934] transition-colors flex items-center gap-1 text-[10px] font-semibold"
                          data-testid={`oc-edit-${o.id}`}>
                          <Pencil size={12}/>
                        </button>
                        {canDelete && (
                          <button onClick={()=>del(o)} title="Enviar a la papelera (recuperable)" data-testid={`oc-delete-${o.id}`} className="text-gray-400 hover:text-rose-600 p-1"><Trash2 size={11}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {!filteredItems.length && <tr><td colSpan={colsToRender.length + 1} className="p-6 text-center text-gray-400">Sin licitaciones que coincidan con los filtros{irrMin > 0 ? ` (IRR ≥ ${irrMin}%)` : ""}</td></tr>}
              </tbody>
            </table>
          </div>
          </>
        }
      </div>

      {editing !== null && <OfertaForm data={editing} onClose={()=>setEditing(null)} onSaved={onSaved} onDeleted={onSaved}/>}
      {showCompetitive && <CompetitiveAnalysisModal oferta={showCompetitive} onClose={()=>setShowCompetitive(null)}/>}
      <MergeSuggestionsModal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        onChanged={reloadMergeCount}
      />
      {showCompliance && <ComplianceCheckModal oferta={showCompliance} onClose={()=>setShowCompliance(null)}/>}
      {showBulk && <CompletitudBulkModal onClose={()=>setShowBulk(false)}/>}
      {showImportModal && <ImportFromExternalModal onClose={()=>setShowImportModal(false)} onImported={()=>{ setShowImportModal(false); load(); }}/>}
      {showMotivoModal && (
        <PendingMotivoModal
          onClose={() => setShowMotivoModal(false)}
          onResolved={() => { reloadHealth(); load(); }}
        />
      )}
      {showConfirmacionModal && (
        <PendingConfirmacionPresentacionModal
          onClose={() => setShowConfirmacionModal(false)}
          onResolved={() => { reloadHealth(); load(); }}
        />
      )}
      {dupesComercialesOpen && (
        <DuplicadosComercialesModal
          onClose={() => setDupesComercialesOpen(false)}
          onMerged={() => { reloadHealth(); load(); }}
        />
      )}
      {showPapelera && (
        <PapeleraModal
          isAdmin={isAdmin}
          onClose={() => setShowPapelera(false)}
          onChanged={() => { load(); loadKpiItems(); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Papelera de licitaciones (feedback David 2026-06): borrado recuperable. Los
// registros eliminados se mueven a `ofertas_comercial_papelera` (salen de todas
// las vistas) y se pueden restaurar aquí. El borrado DEFINITIVO es solo admin.
function PapeleraModal({ isAdmin, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // id en curso

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/papelera/listado`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la papelera");
    }
  };
  useEffect(() => { load(); }, []);

  const restaurar = async (it) => {
    setBusy(it.id);
    try {
      await axios.post(`${API}/ofertas-comercial/papelera/${it.id}/restaurar`);
      toast.success(`Restaurada: ${it.cliente || it.id}`);
      onChanged && onChanged();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar");
    } finally { setBusy(null); }
  };

  const purgar = async (it) => {
    if (!window.confirm(`Borrar DEFINITIVAMENTE "${it.cliente || it.id}"?\n\nEsta acción NO se puede deshacer.`)) return;
    setBusy(it.id);
    try {
      await axios.delete(`${API}/ofertas-comercial/papelera/${it.id}`);
      toast.success("Eliminada definitivamente");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo borrar");
    } finally { setBusy(null); }
  };

  const items = data?.items || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="oc-papelera-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-700"><Trash2 size={15}/> Papelera de licitaciones</h3>
          <button onClick={onClose} data-testid="oc-papelera-close" className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>
        <div className="px-4 py-2 text-[11px] text-gray-500 bg-slate-50 border-b">
          Registros eliminados. Puedes <b>restaurarlos</b> cuando quieras{isAdmin ? "; el borrado definitivo solo lo hace un administrador." : "."}
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
          {!data ? (
            <div className="p-6 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={14}/> Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm" data-testid="oc-papelera-empty">La papelera está vacía</div>
          ) : items.map(it => (
            <div key={it.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`oc-papelera-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800 truncate">{it.cliente || "(sin cliente)"}</div>
                <div className="text-[10px] text-gray-400 truncate">
                  {it.expediente ? `Exp. ${it.expediente} · ` : ""}{it.estado_licitacion || ""}
                  {it.papelera_meta?.deleted_at ? ` · eliminada ${String(it.papelera_meta.deleted_at).slice(0, 10)}` : ""}
                  {it.papelera_meta?.deleted_by ? ` por ${it.papelera_meta.deleted_by}` : ""}
                </div>
              </div>
              <button onClick={() => restaurar(it)} disabled={busy === it.id} data-testid={`oc-papelera-restore-${it.id}`}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-[#007934]/40 text-[#007934] hover:bg-[#007934] hover:text-white transition flex items-center gap-1 disabled:opacity-50">
                {busy === it.id ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>} Restaurar
              </button>
              {isAdmin && (
                <button onClick={() => purgar(it)} disabled={busy === it.id} data-testid={`oc-papelera-purge-${it.id}`}
                  title="Borrar definitivamente (no recuperable)"
                  className="text-[11px] px-2 py-1 rounded-lg text-gray-400 hover:text-rose-600 disabled:opacity-50">
                  <Trash2 size={11}/>
                </button>
              )}
            </div>
          ))}
        </div>
        {data && <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-500">{items.length} en la papelera</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter233 · Modal "Duplicados comerciales (mismo expediente)" — antes sólo
// mostraba la cifra ("no se puede actuar sobre el botón para ver cuales y
// depurarlo"). Reutiliza la MISMA lógica de detección/clasificación/fusión
// que Cola de Calidad (Admin), pero expuesta aquí para admin/offer_manager.
// classification: duplicado_real (mismo objeto → fusión segura) |
// duplicado_sucio (uno con objeto placeholder/basura → fusión segura) |
// lotes_distintos (mismo expediente pero SON lotes reales distintos → NO se
// ofrece fusión, sólo informativo para que el usuario lo confirme a ojo).
function DuplicadosComercialesModal({ onClose, onMerged }) {
  const [data, setData] = useState(null);
  const [merging, setMerging] = useState(null); // `${expediente}|${cliente}` en curso
  const [dismissed, setDismissed] = useState(new Set());
  const [dismissing, setDismissing] = useState(null); // firma_grupo en curso

  const load = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/duplicados-comerciales-detail`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el detalle de duplicados");
    }
  };
  useEffect(() => { load(); }, []);

  const fusionar = async (dup) => {
    const key = `${dup.expediente}|${dup.cliente}`;
    setMerging(key);
    try {
      const r = await axios.post(`${API}/ofertas-comercial/duplicados-comerciales/merge`, {
        keep_id: dup.proposal.keep_id, discard_ids: dup.proposal.discard_ids,
      });
      toast.success(`Fusionado: se eliminaron ${r.data.deleted} registro(s) duplicado(s)`);
      onMerged && onMerged();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo fusionar");
    } finally { setMerging(null); }
  };

  // Feedback producción (2026-10-20): "quito los avisos y siguen apareciendo".
  // El descarte ahora PERSISTE en el backend (por firma_grupo), no sólo en la
  // sesión → no reaparece tras recargar y el contador del banner baja.
  const descartar = async (dup) => {
    setDismissing(dup.firma_grupo);
    try {
      await axios.post(`${API}/ofertas-comercial/duplicados-comerciales/dismiss`, {
        firma_grupo: dup.firma_grupo, expediente: dup.expediente, cliente: dup.cliente,
      });
      setDismissed(prev => new Set([...prev, `${dup.expediente}|${dup.cliente}`]));
      toast.success("Aviso descartado — no volverá a aparecer");
      onMerged && onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descartar el aviso");
    } finally { setDismissing(null); }
  };

  const items = (data?.items || []).filter(
    d => !d.descartado && !dismissed.has(`${d.expediente}|${d.cliente}`)
  );
  const CLS_LABEL = {
    duplicado_real:  { label: "Duplicado real", color: "bg-red-100 text-red-700 border-red-200" },
    duplicado_sucio: { label: "Duplicado (dato basura)", color: "bg-orange-100 text-orange-700 border-orange-200" },
    lotes_distintos: { label: "Lotes distintos (no fusionar)", color: "bg-slate-100 text-slate-600 border-slate-200" },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="dupes-comerciales-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-rose-50 to-white">
          <div>
            <h2 className="font-bold text-slate-800">Duplicados comerciales (mismo expediente)</h2>
            <p className="text-[11px] text-slate-500">Mismo expediente+cliente repetido en Licitaciones. Revisa y fusiona los que sean duplicados reales.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-rose-600 p-1" data-testid="dupes-comerciales-close">
            <X size={18}/>
          </button>
        </div>
        <div className="overflow-auto flex-1 p-3 space-y-2">
          {!data && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Cargando…</div>}
          {data && items.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-8">No hay duplicados comerciales pendientes de revisar.</div>
          )}
          {items.map((dup, i) => {
            const key = `${dup.expediente}|${dup.cliente}`;
            const cls = CLS_LABEL[dup.classification] || CLS_LABEL.lotes_distintos;
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-3" data-testid={`dupes-comerciales-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{dup.cliente || "—"}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{dup.expediente || "—"}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1" title={dup.objeto}>{dup.objeto}</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${cls.color}`}>{cls.label}</span>
                      <span className="text-[10px] text-gray-400">{dup.count} registros repetidos</span>
                    </div>
                    {dup.proposal && (
                      <p className="text-[10px] text-gray-500 mt-1">{dup.proposal.reason}</p>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col gap-1.5 items-end">
                    {dup.proposal ? (
                      <button
                        onClick={() => fusionar(dup)}
                        disabled={merging === key}
                        data-testid={`dupes-comerciales-fusionar-${i}`}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1">
                        {merging === key ? <Loader2 size={10} className="animate-spin"/> : <Check size={10}/>} Fusionar ({dup.count - 1} fuera)
                      </button>
                    ) : (
                      <span className="text-[10px] text-gray-400 italic">Revisar manualmente en el listado</span>
                    )}
                    <button
                      onClick={() => descartar(dup)}
                      disabled={dismissing === dup.firma_grupo}
                      data-testid={`dupes-comerciales-dismiss-${i}`}
                      title="Descartar este aviso de forma permanente (no borra registros, deja de avisar)"
                      className="text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-50 flex items-center gap-1">
                      {dismissing === dup.firma_grupo && <Loader2 size={9} className="animate-spin"/>}
                      Descartar aviso
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-500">
          Pendientes de revisar: <b className="font-mono">{items.length}</b>
          {typeof data?.count === "number" && data.count !== items.length && (
            <span className="text-slate-400"> · {data.count} grupos en total (incl. descartados y lotes distintos)</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default OfertasList;
